// @flow

/**
 * Billing Validation Utilities
 *
 * These utilities help distinguish between different subscription states
 * to provide better user experience and routing logic.
 */

/**
 * Valid subscription statuses from Stripe
 * Reference: https://docs.stripe.com/api/subscriptions/object#subscription_object-status
 */
export const VALID_SUBSCRIPTION_STATUSES = [
  'active',
  'trialing',
  'past_due',
  'canceled',
  'incomplete',
  'incomplete_expired',
  'paused',
  'unpaid'
];

/**
 * Active subscription statuses (user can use the service)
 */
export const ACTIVE_SUBSCRIPTION_STATUSES = [
  'active',
  'trialing'
];

/**
 * Payment issue statuses (user needs to fix payment)
 */
export const PAYMENT_ISSUE_STATUSES = [
  'past_due',
  'incomplete',
  'unpaid'
];

/**
 * Terminal statuses (subscription ended, needs new subscription)
 */
export const TERMINAL_SUBSCRIPTION_STATUSES = [
  'canceled',
  'incomplete_expired'
];

/**
 * Suspended statuses (subscription paused, can be resumed)
 */
export const SUSPENDED_SUBSCRIPTION_STATUSES = [
  'paused'
];

/**
 * Determine if a subscription status is valid from Stripe
 * @param {string} status - The subscription status
 * @returns {boolean} True if the status is a valid Stripe subscription status
 */
export const isValidSubscriptionStatus = (status: string): boolean => {
  return VALID_SUBSCRIPTION_STATUSES.includes(status);
};

/**
 * Determine if a subscription is active and user can use the service
 * @param {string} status - The subscription status
 * @returns {boolean} True if the subscription is active
 */
export const isActiveSubscription = (status: string): boolean => {
  return ACTIVE_SUBSCRIPTION_STATUSES.includes(status);
};

/**
 * Determine if a subscription has payment issues
 * @param {string} status - The subscription status
 * @returns {boolean} True if the subscription has payment issues
 */
export const hasPaymentIssue = (status: string): boolean => {
  return PAYMENT_ISSUE_STATUSES.includes(status);
};

/**
 * Determine if a subscription is terminated and needs new subscription
 * @param {string} status - The subscription status
 * @returns {boolean} True if the subscription is terminated
 */
export const isTerminalSubscription = (status: string): boolean => {
  return TERMINAL_SUBSCRIPTION_STATUSES.includes(status);
};

/**
 * Determine if a subscription is suspended (paused)
 * @param {string} status - The subscription status
 * @returns {boolean} True if the subscription is suspended
 */
export const isSuspendedSubscription = (status: string): boolean => {
  return SUSPENDED_SUBSCRIPTION_STATUSES.includes(status);
};

/**
 * Determine if user has no subscription (never subscribed)
 * @param {Object} billing - The billing data from Redux
 * @returns {boolean} True if user has no subscription history
 */
export const hasNoSubscription = (billing: Object): boolean => {
  if (!billing) return true;

  const { subscriptionStatus, subscriptionId, customerId } = billing;

  // No subscription if status is 'na' and no IDs
  return subscriptionStatus === 'na' && !subscriptionId && !customerId;
};

/**
 * Get the specific subscription action type based on status
 * @param {string} subscriptionStatus - The subscription status
 * @returns {string} Action type for the subscription status
 */
export const getSubscriptionAction = (subscriptionStatus: string): string => {
  const actionMap = {
    'active': 'add_credits',
    'trialing': 'add_payment',
    'past_due': 'update_payment',
    'incomplete': 'complete_setup',
    'unpaid': 'update_payment',
    'paused': 'resume',
    'canceled': 'resubscribe',
    'incomplete_expired': 'start_new',
    'na': 'loading' // Special case for loading state
  };

  return actionMap[subscriptionStatus] || 'view_plans';
};

/**
 * Determine what action the user should take based on their billing state
 * @param {Object} billing - The billing data from Redux
 * @returns {string} Action type: 'recharge' | 'upgrade' | 'billing' | 'loading'
 */
export const getBillingActionType = (billing: Object): string => {
  if (!billing || hasNoSubscription(billing)) return 'upgrade';

  const { subscriptionStatus } = billing;

  // Handle payment issues - redirect to billing screen
  if (subscriptionStatus === 'past_due' || subscriptionStatus === 'unpaid') {
    return 'billing';
  }

  const action = getSubscriptionAction(subscriptionStatus);

  // Map specific actions to legacy action types for backward compatibility
  switch (action) {
    case 'add_credits':
      return 'recharge';
    case 'add_payment':
    case 'update_payment':
    case 'complete_setup':
    case 'resume':
    case 'resubscribe':
    case 'start_new':
    case 'view_plans':
      return 'upgrade';
    case 'loading':
      return 'loading';
    default:
      return 'upgrade';
  }
};

/**
 * Get user-friendly message for billing state
 * @param {Object} billing - The billing data from Redux
 * @returns {string} User-friendly message
 */
export const getBillingStateMessage = (billing: Object): string => {
  if (hasNoSubscription(billing)) {
    return 'Start your subscription to access premium features';
  }

  const { subscriptionStatus } = billing;

  switch (subscriptionStatus) {
    case 'active':
      return 'Subscription active';
    case 'trialing':
      return 'Trial period active';
    case 'past_due':
      return 'Payment failed. Update your payment method to continue';
    case 'incomplete':
      return 'Complete your payment setup to activate subscription';
    case 'unpaid':
      return 'Payment required. Update payment method to restore access';
    case 'paused':
      return 'Subscription paused. Resume to continue using the service';
    case 'canceled':
      return 'Subscription ended. Subscribe again to continue';
    case 'incomplete_expired':
      return 'Setup expired. Start fresh with a new subscription';
    case 'na':
      return 'Loading subscription details...';
    default:
      return `Subscription status: ${subscriptionStatus}. Contact support if you need help`;
  }
};

/**
 * Get button text based on subscription status
 * @param {string} subscriptionStatus - The subscription status
 * @returns {string} Button text for the status
 */
export const getButtonText = (subscriptionStatus: string): string => {
  const buttonTextMap = {
    'active': 'Recharge',
    'trialing': 'Upgrade',
    'past_due': 'Billing',
    'incomplete': 'Upgrade',
    'unpaid': 'Billing',
    'paused': 'Upgrade',
    'canceled': 'Upgrade',
    'incomplete_expired': 'Upgrade',
    'na': 'Upgrade'
  };

  return buttonTextMap[subscriptionStatus] || 'View Plans';
};

